import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, Button, Upload } from 'antd';
import { createSliderItem, updateSliderItem } from '../../services/api';

const { TextArea } = Input;

const SliderForm = ({ initialValues, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      if (initialValues.imageUrl) {
        // Handle both relative and absolute URLs
        const imageUrl = initialValues.imageUrl.startsWith('http')
          ? initialValues.imageUrl
          : `${process.env.REACT_APP_API_URL}${initialValues.imageUrl}`;

        setFileList([{
          uid: '-1',
          name: 'Current Image',
          status: 'done',
          url: imageUrl,
        }]);
      }
    }
  }, [initialValues, form]);

  const onFinish = async (values) => {
    try {
      setUploading(true);

      // Validate file for new items
      if (!initialValues?.id && !fileList[0]) {
        toast.error('Please select an image file');
        setUploading(false);
        return;
      }

      const formData = new FormData();

      // Append file first
      const file = fileList[0];
      if (file?.originFileObj) {
        formData.append('image', file.originFileObj);
      }

      // Then append other fields
      formData.append('title', values.title);
      formData.append('subtitle', values.subtitle || '');
      formData.append('order', values.order.toString());

      if (initialValues?.id) {
        await updateSliderItem(initialValues.id, formData);
        toast.success('Slider item updated successfully');
      } else {
        await createSliderItem(formData);
        toast.success('Slider item created successfully');
      }
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save slider item');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      if (!file.type.startsWith('image/')) {
        toast.error('You can only upload image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        toast.error('Image must be smaller than 5MB!');
        return false;
      }
      setFileList([{ originFileObj: file }]);
      return false;
    },
    fileList,
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      encType="multipart/form-data"
    >
      <Form.Item
        name="title"
        label="Title"
        rules={[
          { required: true, message: 'Please input title!' },
          { max: 100, message: 'Title cannot exceed 100 characters' }
        ]}
      >
        <Input maxLength={100} showCount />
      </Form.Item>

      <Form.Item
        name="subtitle"
        label="Subtitle"
        rules={[
          { max: 200, message: 'Subtitle cannot exceed 200 characters' }
        ]}
      >
        <TextArea maxLength={200} showCount autoSize={{ minRows: 2, maxRows: 4 }} />
      </Form.Item>

      <Form.Item
        label="Image"
        required
        rules={[{
          validator: () => {
            if (!fileList[0] && !initialValues?.imageUrl) {
              return Promise.reject('Please upload an image!');
            }
            return Promise.resolve();
          }
        }]}
      >
        <Upload {...uploadProps} listType="picture" maxCount={1}>
          <Button icon={<UploadOutlined />}>Select Image</Button>
        </Upload>
      </Form.Item>

      <Form.Item
        name="order"
        label="Display Order"
        rules={[{ required: true, message: 'Please input display order!' }]}
      >
        <InputNumber min={0} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={uploading}>
          {initialValues?.id ? 'Update' : 'Create'}
        </Button>
        <Button onClick={onCancel} style={{ marginLeft: 8 }}>
          Cancel
        </Button>
      </Form.Item>
    </Form>
  );
};

export default SliderForm;
